import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getMainNavItems, type TabId } from './navigationConfig';
import { BRAND_NAME, BRAND_TAGLINE } from '../../config/branding';
import { useAppSelector } from '../../store';

interface DesktopSidebarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  className?: string;
  /** Controlled: must match `<main>` horizontal offset (w-16 / w-60). */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

/**
 * DesktopSidebar - Desktop navigation sidebar
 *
 * Features:
 * - Logo with gradient background
 * - Full navigation with icons and labels
 * - Active state indicators
 * - Hidden on mobile (md:flex)
 */
const DesktopSidebar: React.FC<DesktopSidebarProps> = React.memo(
  ({
    activeTab,
    onTabChange,
    isAuthenticated,
    isAdmin,
    className,
    collapsed,
    onCollapsedChange,
  }) => {
    const { t, i18n } = useTranslation();
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);
    const isRtl = i18n.language?.startsWith('he') || i18n.language?.startsWith('ar');
    // Re-render when the feature-status probe settles so Trade Diary nav appears.
    const positionDiaryFeatureEnabled = useAppSelector(
      (s) => s.positions.featureEnabled
    );
    void positionDiaryFeatureEnabled;

    // Filter nav items based on auth state, disabled, and feature flags
    // BUG-MA-006 FIX: Also filter by MA Touch Alerts feature flag
    const visibleNavItems = getMainNavItems().filter((item) => {
      if (item.disabled) return false;
      if (item.requiresAuth && !isAuthenticated) return false;
      if (item.requiresAdmin && !isAdmin) return false;
      if (item.hideForAdmin && isAdmin) return false;
      return true;
    });

    const handleLanguageChange = (lang: string) => {
      i18n.changeLanguage(lang);
      setShowLanguageMenu(false);
    };

    return (
      <aside
        className={cn(
          'fixed top-0 bottom-0',
          isRtl ? 'right-0' : 'left-0',
          collapsed ? 'w-16' : 'w-60',
          // Institutional Editor: tonal shift - container-low vs surface
          'bg-surface-container-low',
          // No-line rule: NO borders, tonal shifts only
          'flex flex-col',
          'z-30',
          'hidden md:flex', // Hide on mobile
          'transition-all duration-200',
          className
        )}
      >
        {/* Logo Section */}
        <div
          className={cn(
            'p-4',
            // No-line rule: tonal shift instead of border
            'border-b border-transparent',
            collapsed && 'p-2'
          )}
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)'
          }}
        >
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo-app.png`}
              alt={BRAND_NAME}
              className={cn(
                'flex-shrink-0 object-contain',
                collapsed ? 'h-10 w-10' : 'h-10 w-10'
              )}
            />
            {!collapsed && (
              <div className="space-y-0.5 min-w-0">
                <div className="text-base font-semibold text-text-primary font-display leading-tight">
                  {BRAND_NAME}
                </div>
                <p className="text-xs text-text-muted leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                  {BRAND_TAGLINE}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav
          className={cn('flex-1 overflow-y-auto', collapsed ? 'p-1' : 'p-3')}
        >
          <ul
            className="space-y-1"
            role="tablist"
            aria-label="Primary navigation"
          >
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <li key={item.id}>
                  <button
                    onClick={() => onTabChange(item.id as TabId)}
                    role="tab"
                    aria-selected={isActive ? 'true' : 'false'}
                    className={cn(
                      'w-full flex items-center gap-3',
                      collapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3',
                      'rounded-xl',
                      'text-sm font-medium',
                      'transition-all duration-200',
                      isActive
                        ? 'bg-accent-primary/15 text-accent-primary'
                        : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface',
                      // Gold accent bar on active (left for LTR, right for RTL)
                      isActive && (isRtl ? 'border-e-2 border-accent-primary' : 'border-s-2 border-accent-primary')
                    )}
                    aria-current={isActive ? 'page' : undefined}
                    title={item.beta ? t('nav.betaTooltip', { defaultValue: 'Beta feature - may still contain bugs.' }) : collapsed ? t(item.labelKey) : undefined}
                  >
                    <span className="relative flex-shrink-0">
                      <Icon className="h-5 w-5" />
                      {collapsed && item.beta && (
                        <span className="absolute -top-1 -end-1 h-2 w-2 rounded-full bg-accent-primary" aria-hidden="true" />
                      )}
                    </span>
                    {!collapsed && (
                      <span className="whitespace-nowrap flex items-center gap-1.5">
                        {t(item.labelKey)}
                        {item.beta && (
                          <span className="text-xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-primary/20 text-accent-primary leading-none">
                            {t('nav.beta', { defaultValue: 'Beta' })}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div
          className={cn(
            // No-line rule: tonal shift instead of border
            'border-t border-transparent',
            collapsed ? 'p-2' : 'p-4'
          )}
          style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.15) 0%, transparent 100%)'
          }}
        >
          {/* Language Selector */}
          <div className="relative mb-2">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className={cn(
                'w-full flex items-center gap-2',
                collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2',
                'text-on-surface-variant hover:text-on-surface',
                'hover:bg-surface-container-high rounded-lg',
                'transition-colors duration-200'
              )}
              title={t('language.selectLanguage')}
            >
              <Globe className="h-5 w-5" />
              {!collapsed && (
                <span className="text-sm font-medium">
                  {i18n.language?.startsWith('he') ? 'עברית' : i18n.language?.startsWith('ar') ? 'العربية' : i18n.language?.startsWith('ru') ? 'Русский' : 'English'}
                </span>
              )}
            </button>

            {/* Language Dropdown */}
            {showLanguageMenu && (
              <div
                className={cn(
                  'absolute bottom-full mb-2 left-1/2 -translate-x-1/2',
                  // Glass panel styling
                  'bg-surface-container/95 backdrop-blur-xl',
                  'border border-outline-variant/20 rounded-xl shadow-lg shadow-black/30',
                  'overflow-hidden z-50 min-w-[120px]'
                )}
              >
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={cn(
                    'w-full flex items-center justify-center px-4 py-2.5 text-sm',
                    'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high',
                    'transition-colors duration-200',
                    (i18n.language === 'en' || i18n.language?.startsWith('en')) && 'text-accent-primary bg-accent-primary/10'
                  )}
                >
                  English
                </button>
                <button
                  onClick={() => handleLanguageChange('he')}
                  className={cn(
                    'w-full flex items-center justify-center px-4 py-2.5 text-sm',
                    'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high',
                    'transition-colors duration-200',
                    i18n.language?.startsWith('he') && 'text-accent-primary bg-accent-primary/10'
                  )}
                >
                  עברית
                </button>
                <button
                  onClick={() => handleLanguageChange('ar')}
                  className={cn(
                    'w-full flex items-center justify-center px-4 py-2.5 text-sm',
                    'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high',
                    'transition-colors duration-200',
                    i18n.language?.startsWith('ar') && 'text-accent-primary bg-accent-primary/10'
                  )}
                >
                  العربية
                </button>
                <button
                  onClick={() => handleLanguageChange('ru')}
                  className={cn(
                    'w-full flex items-center justify-center px-4 py-2.5 text-sm',
                    'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high',
                    'transition-colors duration-200',
                    i18n.language?.startsWith('ru') && 'text-accent-primary bg-accent-primary/10'
                  )}
                >
                  Русский
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => onCollapsedChange(!collapsed)}
            className={cn(
              'w-full flex items-center justify-center',
              'text-on-surface-variant hover:text-on-surface',
              'hover:bg-surface-container-high rounded-lg',
              'transition-colors duration-200',
              collapsed ? 'p-2' : 'p-2'
            )}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            {collapsed ? (
              isRtl ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />
            ) : (
              isRtl ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />
            )}
          </button>
        </div>
      </aside>
    );
  }
);

DesktopSidebar.displayName = 'DesktopSidebar';

export default DesktopSidebar;
